import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Logout from "@mui/icons-material/Logout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpRightAndDownLeftFromCenter,
  faUserCog,
} from "@fortawesome/free-solid-svg-icons";
import "./../../../styles/AvatarDropdown.css";
import { useAuth } from "../../../context/AuthContext";

const INACTIVITY_TIMEOUT = 20 * 60 * 1000;

// Function to get random avatar color
const getAvatarColor = (userName) => {
  const colors = [
    "avatar-color-1",
    "avatar-color-2",
    "avatar-color-3",
    "avatar-color-4",
    "avatar-color-5",
    "avatar-color-6",
  ];
  const index = userName.charCodeAt(0) % colors.length;
  return colors[index];
};

const AvatarDropdown = ({
  setIsLoggedIn = () => {},
  setUserDetails = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { user, role: userRole, logout: authLogout } = useAuth();
  const userName = user?.username || "";
  const isAdmin = userRole !== "ROLE_USER";
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleLogout = useCallback(async () => {
    try {
      await authLogout();
    } catch (error) {
      console.error("Logout request failed", error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      setIsLoggedIn(false);
      setUserDetails({});
      navigate("/login");
    }
  }, [authLogout, navigate, setIsLoggedIn, setUserDetails]);

  const resetInactivityTimer = useCallback(() => {
    localStorage.setItem("lastActivity", Date.now().toString());
  }, []);

  const checkInactivity = useCallback(() => {
    const lastActivity = parseInt(localStorage.getItem("lastActivity") || "0");
    const currentTime = Date.now();
    if (currentTime - lastActivity > INACTIVITY_TIMEOUT) {
      handleLogout();
      localStorage.setItem("sessionExpired", "true");
    }
  }, [handleLogout]);

  useEffect(() => {
    resetInactivityTimer();
    const activityEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "mousemove",
    ];

    activityEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );
    const interval = setInterval(checkInactivity, 60000);
    return () => {
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
      clearInterval(interval);
    };
  }, [resetInactivityTimer, checkInactivity]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen().catch(console.error);
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(console.error);
      setIsFullscreen(false);
    }
  };

  return (
    <>
      <Box className="avatar-dropdown-container">
        <Tooltip title="Account" arrow placement="bottom">
          <IconButton
            onClick={handleClick}
            size="small"
            className="avatar-button"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Avatar className={`avatar-icon ${getAvatarColor(userName)}`}>
              {userName?.charAt(0)?.toUpperCase() || "U"}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        className="avatar-menu"
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box className="avatar-header">
          <Typography className="avatar-header-caption">
            Welcome back
          </Typography>
          <Typography className="avatar-header-username">{userName}</Typography>
          <Box className="avatar-status-badge">
            <Box
              className={`avatar-status-dot ${
                isAdmin ? "avatar-status-dot-admin" : "avatar-status-dot-user"
              }`}
            />
            <Typography className="avatar-status-text">
              {isAdmin ? "Admin" : "User"}
            </Typography>
          </Box>
        </Box>

        <Box>
          <MenuItem onClick={() => navigate("/adminpanel")}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faUserCog} />
            </ListItemIcon>
            <Typography>Settings</Typography>
          </MenuItem>

          <MenuItem onClick={toggleFullscreen}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
            </ListItemIcon>
            <Typography>Fullscreen</Typography>
          </MenuItem>

          {/* <Box className="avatar-menu-divider" /> */}

          <MenuItem onClick={handleLogout} className="avatar-menu-item-logout">
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <Typography>Logout</Typography>
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
};

export default AvatarDropdown;
